import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/api";
import StatCard from "@/components/StatCard";
import LiveTicker from "@/components/LiveTicker";
import ChallengeCard from "@/components/ChallengeCard";
import TargetIndicator from "@/components/TargetIndicator";
import TargetTicker from "@/components/TargetTicker";
import CrowningPlatform from "@/components/CrowningPlatform";
import SchoolsLeaderboard from "@/components/SchoolsLeaderboard";
import AnomalousHours from "@/components/AnomalousHours";
import { GlassCard, EmptyState } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import { School, HandHeart, Users, Clock, Activity, Coins, Trophy, GraduationCap, Briefcase, Target } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const filters = [
  { key: "", label: "جميع المدارس" },
  { key: "بنين", label: "مدارس البنين" },
  { key: "بنات", label: "مدارس البنات" },
];

const authFilters = [
  { key: "", label: "كل المدارس" },
  { key: "حكومي", label: "حكومي" },
  { key: "أهلي", label: "أهلي" },
  { key: "عالمي وأجنبي", label: "عالمي وأجنبي" },
];

function TopList({ title, icon: Icon, data }) {
  const medals = ["#FFD700", "#C0C0C0", "#CD7F32"];
  return (
    <GlassCard>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="text-amber-600" size={20} />
        <h3 className="font-head font-bold text-lg text-stone-800">{title}</h3>
      </div>
      {!data.length ? (
        <p className="text-stone-500 text-sm py-4 text-center">لا توجد بيانات بعد</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pe-1">
          {data.map((v, i) => (
            <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-amber-100/70 transition">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                style={{ background: i < 3 ? medals[i] : "rgba(212,175,55,0.15)", color: i < 3 ? "#1A120B" : "#D4AF37" }}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-stone-800 font-medium truncate">{v.name}</div>
                <div className="text-xs text-stone-500 truncate">{v.school_name}</div>
              </div>
              <div className="text-left shrink-0">
                <div className="text-sm font-bold text-amber-600">{v.hours} س</div>
                <div className="text-[10px] text-stone-400">{v.participations} مشاركة</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

export default function AdminDashboard() {
  const [type, setType] = useState("");
  const [authority, setAuthority] = useState("");
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    const parts = [];
    if (type) parts.push(`type=${encodeURIComponent(type)}`);
    if (authority) parts.push(`authority=${encodeURIComponent(authority)}`);
    const q = parts.length ? `?${parts.join("&")}` : "";
    const st = type ? `&school_type=${encodeURIComponent(type)}` : "";
    api.get(`/stats/admin${q}`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/stats/top-volunteers?type=student${st}`).then((r) => setStudents(r.data)).catch(() => {});
    api.get(`/stats/top-volunteers?type=staff${st}`).then((r) => setStaff(r.data)).catch(() => {});
  }, [type, authority]);

  if (!stats) return <div className="text-stone-500">جارٍ التحميل...</div>;

  const g = type === "بنات"
    ? { students: "عدد الطالبات", vols: "المتطوعات الفعليّات", group: "الطالبات", singular: "طالبةً متطوعةً", who: "طالبة" }
    : type === "بنين"
    ? { students: "عدد الطلاب", vols: "المتطوعون الفعليّون", group: "الطلاب", singular: "طالباً متطوعاً", who: "طالب" }
    : { students: "عدد الطلبة", vols: "المتطوعون الفعليّون", group: "الطلبة", singular: "طالباً متطوعاً", who: "طالب" };
  const cards = [
    { icon: School, label: "إجمالي المدارس", value: stats.total_schools, accent: "#0EA5E9" },
    { icon: HandHeart, label: "إجمالي الفرص", value: stats.total_opportunities, accent: "#38BDF8" },
    { icon: GraduationCap, label: g.students, value: stats.students_total, accent: "#0EA5E9" },
    { icon: Users, label: g.vols, value: stats.real_volunteers ?? stats.total_volunteers, accent: "#0284C7" },
    { icon: Clock, label: "إجمالي الساعات", value: stats.total_hours, accent: "#F59E0B" },
    { icon: Coins, label: "العائد الاقتصادي", value: stats.economic_value, suffix: "ر.س", accent: "#D4AF37" },
  ];

  const rankedByExecuted = [...stats.schools].sort((a, b) => (b.stats.executed_hours || 0) - (a.stats.executed_hours || 0));
  const chartData = rankedByExecuted.slice(0, 10).map((s) => ({ name: s.name, hours: s.stats.executed_hours || 0 }));

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass rounded-2xl p-6">
        <p className="text-sm text-stone-500">لوحة أداء مدارس الإدارة العامة للتعليم بمنطقة حائل</p>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
          <h2 className="text-2xl sm:text-3xl font-head font-bold gold-text">
            {type ? `مؤشرات ${filters.find((f) => f.key === type)?.label}` : "المؤشرات العامة لجميع المدارس"}
            {authority ? ` — ${authority}` : ""}
          </h2>
          <div className="flex gap-2">
            {filters.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key || "all"}`}
                onClick={() => setType(f.key)}
                className={`px-4 py-2 rounded-xl text-sm transition ${
                  type === f.key ? "btn-gold" : "glass glass-hover text-stone-600"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-amber-400/15">
          <span className="text-xs text-stone-500 self-center ml-1">السلطة:</span>
          {authFilters.map((f) => (
            <button
              key={f.key}
              data-testid={`auth-filter-${f.key || "all"}`}
              onClick={() => setAuthority(f.key)}
              className={`px-4 py-1.5 rounded-xl text-sm transition ${
                authority === f.key ? "bg-sky-600 text-white" : "glass glass-hover text-stone-600"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </motion.div>

      <LiveTicker />

      <TargetTicker />

      <InspireBanner />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c, i) => <StatCard key={c.label} {...c} testId={`admin-stat-${i}`} delay={i * 0.05} />)}
      </div>

      {!type && stats.by_gender && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="gender-breakdown">
          {[
            { key: "إجمالي", label: "الإجمالي (بنين + بنات)", grad: "from-amber-500 to-yellow-500", chip: "bg-amber-500/10", sw: "طلبة",
              d: { schools: stats.total_schools, students_total: stats.students_total, student_volunteers: stats.student_volunteers,
                   opportunities: stats.total_opportunities, executed_hours: stats.total_executed_hours,
                   economic_value: stats.economic_value, participation_rate: stats.participation_rate } },
            { key: "بنين", label: "مدارس البنين", grad: "from-sky-500 to-sky-600", chip: "bg-sky-500/10", sw: "طلاب", d: stats.by_gender["بنين"] || {} },
            { key: "بنات", label: "مدارس البنات", grad: "from-rose-500 to-rose-600", chip: "bg-rose-500/10", sw: "طالبات", d: stats.by_gender["بنات"] || {} },
          ].map((gc) => {
            const d = gc.d;
            const rows = [
              { icon: School, label: "المدارس", value: d.schools },
              { icon: GraduationCap, label: `عدد ال${gc.sw}`, value: d.students_total },
              { icon: Users, label: "المتطوعون الفعليّون", value: d.student_volunteers },
              { icon: HandHeart, label: "الفرص", value: d.opportunities },
              { icon: Clock, label: "الساعات المنفّذة", value: d.executed_hours },
              { icon: Coins, label: "العائد (ر.س)", value: d.economic_value },
            ];
            return (
              <div key={gc.key} data-testid={`gender-card-${gc.key}`} className="glass rounded-2xl overflow-hidden">
                <div className={`bg-gradient-to-l ${gc.grad} text-white px-5 py-3 flex items-center justify-between`}>
                  <h3 className="font-head font-bold">{gc.label}</h3>
                  <span className="text-xs sm:text-sm bg-white/20 rounded-full px-3 py-0.5 flex items-center gap-1"><Target size={14} /> تحقيق المستهدف {d.participation_rate || 0}%</span>
                </div>
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {rows.map((r) => (
                    <div key={r.label} className={`rounded-xl p-3 ${gc.chip}`}>
                      <div className="flex items-center gap-1.5 text-[11px] text-stone-500"><r.icon size={14} /> {r.label}</div>
                      <div className="text-lg font-bold mt-1 text-stone-800">{r.value ?? 0}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TargetIndicator rate={stats.participation_rate} target={stats.target || 90} testId="admin-target"
        studentsTotal={stats.students_total} studentVols={stats.student_volunteers}
        groupNoun={g.group} singularVol={g.singular}
        subtitle={`${g.vols} ${stats.student_volunteers} من أصل ${stats.students_total} ${g.who} في جميع المدارس`} />

      <SchoolsLeaderboard schools={stats.schools} />

      <AnomalousHours />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <GlassCard className="lg:col-span-2">
          <h3 className="font-head font-bold text-lg text-stone-800 mb-4">ترتيب المدارس حسب الساعات المنفّذة</h3>
          {!chartData.length ? <EmptyState text="لا توجد مدارس بعد" icon={School} /> : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} layout="vertical" margin={{ right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(180,120,20,0.18)" />
                <XAxis type="number" stroke="rgba(68,64,60,0.75)" fontSize={12} />
                <YAxis type="category" dataKey="name" stroke="rgba(68,64,60,0.75)" fontSize={11} width={110} />
                <Tooltip cursor={{ fill: "rgba(180,120,20,0.07)" }} contentStyle={{ background: "#241710", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#FDF8F0" }} />
                <Bar dataKey="hours" name="الساعات" fill="#D4AF37" radius={[0, 6, 6, 0]} maxBarSize={38} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </GlassCard>
        <ChallengeCard />
      </div>

      <CrowningPlatform adminEdit />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopList title="أعلى 25 متطوعاً — الطلاب والطالبات" icon={GraduationCap} data={students} />
        <TopList title="أعلى 25 — الموظفون والموظفات" icon={Briefcase} data={staff} />
      </div>

      <GlassCard>
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="text-amber-600" size={20} />
          <h3 className="font-head font-bold text-lg text-stone-800">تفاصيل المدارس</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-stone-500 text-right border-b border-amber-400/15">
                <th className="py-3 px-2 font-medium">#</th>
                <th className="py-3 px-2 font-medium">المدرسة</th>
                <th className="py-3 px-2 font-medium">النوع</th>
                <th className="py-3 px-2 font-medium">الفرص</th>
                <th className="py-3 px-2 font-medium">المتطوعون</th>
                <th className="py-3 px-2 font-medium">مشاركة الطلاب</th>
                <th className="py-3 px-2 font-medium">الساعات</th>
                <th className="py-3 px-2 font-medium">الساعات المنفّذة</th>
                <th className="py-3 px-2 font-medium">العائد (ر.س)</th>
              </tr>
            </thead>
            <tbody>
              {stats.schools.map((s, i) => (
                <tr key={s.id} className="border-b border-amber-400/10 hover:bg-amber-100/50 transition">
                  <td className="py-3 px-2 text-amber-600 font-bold">{i + 1}</td>
                  <td className="py-3 px-2 text-stone-800">{s.name}</td>
                  <td className="py-3 px-2"><span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-700">{s.type}</span></td>
                  <td className="py-3 px-2 text-stone-700">{s.stats.opportunities}</td>
                  <td className="py-3 px-2 text-stone-700">{s.stats.volunteers}</td>
                  <td className="py-3 px-2"><span className="font-semibold" style={{ color: (s.stats.participation_rate || 0) >= 90 ? "#10B981" : (s.stats.participation_rate || 0) >= 60 ? "#D4AF37" : "#F59E0B" }}>{s.stats.participation_rate || 0}%</span></td>
                  <td className="py-3 px-2 text-amber-600 font-semibold">{s.stats.hours}</td>
                  <td className="py-3 px-2 text-emerald-600 font-bold">{s.stats.executed_hours ?? 0}</td>
                  <td className="py-3 px-2 text-stone-700">{s.stats.economic_value}</td>
                </tr>
              ))}
              {!stats.schools.length && (
                <tr><td colSpan={9} className="py-10 text-center text-stone-500">لا توجد مدارس</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}
